import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useI18n } from '../i18n';
import api from '../services/api';
import fmt from '../quotes/catalog/format';

const CURRENCIES = ['MXN', 'USD', 'CLP', 'COP'];
const BLANK = { name: '', description: '', unit: '', prices: {}, platform: '', interval: '', license: '' };

function PriceRow({ prices, disabled, onChange }) {
  return (
    <Box sx={{
      display: 'grid', gap: 1.5, mt: 1.5,
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
    }}>
      {CURRENCIES.map((c) => (
        <Box key={c}>
          <Typography sx={{
            fontSize: 10.5, fontWeight: 500, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: '#847782', mb: 0.3,
          }}>{c}</Typography>
          <TextField
            size="small" fullWidth type="number" disabled={disabled}
            value={prices?.[c] ?? ''}
            onChange={(e) => onChange({ ...prices, [c]: e.target.value })}
            inputProps={{ min: 0, step: 1 }}
          />
        </Box>
      ))}
    </Box>
  );
}

export default function ProductsConsole() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = useCallback(() => {
    api.get('/api/products', { params: { all: true } })
      .then((res) => { setItems(res.data); setError(null); })
      .catch((err) => setError(err.response?.status === 403
        ? t('products.forbidden') : t('products.loadError')));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  async function save(product, changes) {
    setBusy(product.id);
    try {
      await api.patch(`/api/products/${product.id}`, changes);
      setDraft(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('products.saveError'));
    } finally {
      setBusy(null);
    }
  }

  async function create() {
    setBusy('new');
    try {
      await api.post('/api/products', creating);
      setCreating(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('products.saveError'));
    } finally {
      setBusy(null);
    }
  }

  if (error && !items) return <Alert severity="error">{error}</Alert>;
  if (!items) return <Skeleton variant="rectangular" height={320} />;

  return (
    <Box>
      <Typography sx={{ color: '#847782', fontSize: 13.5, mb: 2, maxWidth: '72ch' }}>
        {t('products.intro')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {creating ? (
        <Box sx={{ border: '1px solid #E4E0E2', borderRadius: '5px', p: 2, mb: 2, backgroundColor: '#FFF8F8' }}>
          <TextField size="small" fullWidth label={t('products.name')} sx={{ mb: 1.5 }}
            value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} />
          <TextField size="small" fullWidth multiline minRows={3} label={t('products.description')}
            value={creating.description}
            onChange={(e) => setCreating({ ...creating, description: e.target.value })} />
          <TextField size="small" label={t('products.unit')} sx={{ mt: 1.5, width: 200 }}
            value={creating.unit} onChange={(e) => setCreating({ ...creating, unit: e.target.value })} />
          <PriceRow prices={creating.prices} disabled={busy === 'new'}
            onChange={(prices) => setCreating({ ...creating, prices })} />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button size="small" variant="contained" disabled={busy === 'new' || !creating.name.trim()}
              onClick={create}>{t('common.save')}</Button>
            <Button size="small" onClick={() => setCreating(null)} sx={{ color: '#847782' }}>
              {t('common.close')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Button variant="contained" size="small" sx={{ mb: 2 }}
          onClick={() => setCreating({ ...BLANK, prices: {} })}>
          {t('products.new')}
        </Button>
      )}

      {items.map((p) => {
        const editing = draft?.id === p.id;
        const current = editing ? draft : p;
        return (
          <Accordion key={p.id} disableGutters
            sx={{ border: '1px solid #E4E0E2', mb: 1, '&:before': { display: 'none' }, boxShadow: 'none' }}>
            <AccordionSummary sx={{ px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, width: '100%', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, opacity: p.active ? 1 : 0.55 }}>
                  {p.name}
                </Typography>
                {!p.active && (
                  <Chip size="small" label={t('products.inactive')}
                    sx={{ height: 18, fontSize: 10, backgroundColor: '#847782', color: '#fff', fontWeight: 600 }} />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Typography sx={{ fontSize: 12.5, color: '#847782', fontVariantNumeric: 'tabular-nums' }}>
                  {p.prices?.MXN != null ? fmt.getCurrencyFormat(p.prices.MXN, 'MXN') : '—'}
                  {p.prices?.USD != null ? ` · ${fmt.getCurrencyFormat(p.prices.USD, 'USD')}` : ''}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pt: 0 }}>
              <TextField size="small" fullWidth label={t('products.name')} sx={{ mb: 1.5 }}
                value={current.name} disabled={busy === p.id}
                onChange={(e) => setDraft({ ...current, id: p.id, name: e.target.value })} />
              <TextField size="small" fullWidth multiline minRows={4} label={t('products.description')}
                value={current.description || ''} disabled={busy === p.id}
                onChange={(e) => setDraft({ ...current, id: p.id, description: e.target.value })} />
              <TextField size="small" label={t('products.unit')} sx={{ mt: 1.5, width: 200 }}
                value={current.unit || ''} disabled={busy === p.id}
                onChange={(e) => setDraft({ ...current, id: p.id, unit: e.target.value })} />

              <PriceRow prices={current.prices} disabled={busy === p.id}
                onChange={(prices) => setDraft({ ...current, id: p.id, prices })} />

              <Box sx={{ display: 'flex', gap: 1.5, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button size="small" variant="contained" disabled={busy === p.id || !editing}
                  onClick={() => save(p, {
                    name: draft.name, description: draft.description,
                    unit: draft.unit, prices: draft.prices,
                  })}>{t('common.save')}</Button>
                {editing && (
                  <Button size="small" onClick={() => setDraft(null)} sx={{ color: '#847782' }}>
                    {t('common.close')}
                  </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <FormControlLabel
                  control={<Switch checked={p.active} disabled={busy === p.id}
                    onChange={(e) => save(p, { active: e.target.checked })} />}
                  label={<Typography sx={{ fontSize: 13 }}>{t('products.active')}</Typography>}
                />
              </Box>
              <Typography sx={{ fontSize: 11.5, color: '#847782', mt: 1 }}>
                {t('products.historyNote')}
              </Typography>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
